import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ChevronDown, ChevronRight, RotateCcw, Camera, History as HistoryIcon } from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { formatCalendarDate, formatTime } from '@/lib/dates';

/**
 * Change history for one schedule version (Mike item 12).
 *
 * Reads schedule_move_log — the same rows the undo stack walks — grouped by
 * the batch they were written in, so a placement run of 13 games is one line
 * and a drag is one line. Snapshots are interleaved by time, visually
 * distinct, each with its own Restore. History is a record, not a queue: an
 * undone change stays in the list, struck through.
 */

type Cell = { court_name: string; date: string; start_time: string } | null;

type Move = {
  id: string;
  game_id: string;
  division: string | null;
  game_number: number | null;
  from: Cell;
  to: Cell;
  /** A bracket move changed where the game sits on the bracket, not on a court. */
  from_bracket?: { round: number; position: number | null } | null;
  to_bracket?: { round: number; position: number | null } | null;
  /** The game that was actually dragged; its siblings only renumbered. */
  is_lead?: boolean;
  /** Set when a change was made by support rather than by an operator. */
  note?: string | null;
  undone_at: string | null;
};

type Batch = {
  batch_id: string;
  source: string;
  source_label: string;
  actor: string | null;
  created_at: string;
  undone_at: string | null;
  all_undone: boolean;
  count: number;
  moves: Move[];
};

const whenLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  return sameDay ? `today ${time}` : `${formatCalendarDate(d.toISOString().slice(0, 10), 'EEE MMM d')} ${time}`;
};

const cellLabel = (c: Cell) =>
  c ? `${c.court_name}, ${formatCalendarDate(c.date, 'EEE')} ${formatTime(c.start_time)}` : 'off the board';

/** Where a move took the game: a court and a time, or a place on a bracket. */
// Positions arrive 1-based from the server — the same number the move
// confirmation and the undo message show. Never add one again here.
const spotLabel = (c: Cell, bracket?: { round: number; position: number | null } | null) =>
  bracket
    ? (typeof bracket.position === "number"
      ? `round ${bracket.round}, position ${bracket.position}`
      : `round ${bracket.round}, position not recorded`)
    : cellLabel(c);

const gameLabel = (m: Move) =>
  `${m.division ? `${m.division} ` : ''}Game ${m.game_number ?? '?'}`;

export default function ScheduleHistoryPanel({
  eventId,
  open,
  onOpenChange,
  snapshots = [],
  onRestore,
  refreshToken = 0,
}: {
  eventId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  snapshots?: any[];
  onRestore?: (snapshot: any) => void;
  refreshToken?: number;
}) {
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [day, setDay] = useState('all');
  const [division, setDivision] = useState('all');

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'move_log_list', event_id: eventId },
      });
      if (res?.ok) setBatches(res.data?.batches ?? []);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { if (open) void load(); }, [open, load, refreshToken]);

  const dayOptions = useMemo(() => {
    const set = new Set<string>();
    for (const b of batches) {
      for (const m of b.moves) {
        if (m.to?.date) set.add(m.to.date);
        if (m.from?.date) set.add(m.from.date);
      }
    }
    return [...set].sort();
  }, [batches]);

  const divisionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const b of batches) for (const m of b.moves) if (m.division) set.add(m.division);
    return [...set].sort();
  }, [batches]);

  const visible = useMemo(() => batches
    .map((b) => ({
      ...b,
      moves: b.moves.filter((m) => {
        if (division !== 'all' && m.division !== division) return false;
        if (day !== 'all' && m.to?.date !== day && m.from?.date !== day) return false;
        return true;
      }),
    }))
    .filter((b) => b.moves.length > 0), [batches, day, division]);

  // Moves and snapshots on one timeline, newest first.
  const timeline = useMemo(() => {
    const items: Array<{ kind: 'move'; at: string; batch: Batch } | { kind: 'snapshot'; at: string; snap: any }> = [
      ...visible.map((b) => ({ kind: 'move' as const, at: b.created_at, batch: b })),
      ...(snapshots ?? []).map((s: any) => ({ kind: 'snapshot' as const, at: s.created_at, snap: s })),
    ];
    return items.sort((a, b) => (a.at < b.at ? 1 : -1));
  }, [visible, snapshots]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-xl">
        <SheetHeader className="shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <HistoryIcon className="h-4 w-4" /> History
          </SheetTitle>
          <SheetDescription>
            Every change to this schedule, newest first. Saved arrangements sit on the same timeline.
          </SheetDescription>
        </SheetHeader>

        <div className="flex shrink-0 items-center gap-2 py-3">
          <Select value={day} onValueChange={setDay}>
            <SelectTrigger className="h-8 w-[150px]" aria-label="Filter history by day">
              <SelectValue placeholder="All days" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All days</SelectItem>
              {dayOptions.map((d) => (
                <SelectItem key={d} value={d}>{formatCalendarDate(d, 'EEE MMM d', d)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={division} onValueChange={setDivision}>
            <SelectTrigger className="h-8 w-[150px]" aria-label="Filter history by division">
              <SelectValue placeholder="All divisions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All divisions</SelectItem>
              {divisionOptions.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={() => void load()} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {loading && timeline.length === 0 ? (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading the history…
            </div>
          ) : timeline.length === 0 ? (
            <p className="py-8 text-sm text-muted-foreground">
              No changes yet. Moves you make on the grid will show up here.
            </p>
          ) : (
            <ul className="divide-y">
              {timeline.map((item) => {
                if (item.kind === 'snapshot') {
                  const s = item.snap;
                  return (
                    <li key={`snap-${s.id}`} className="flex items-start gap-3 rounded-md bg-muted/40 px-3 py-3 text-sm">
                      <Camera className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{s.label ?? 'Saved arrangement'}</div>
                        <div className="text-xs text-muted-foreground">
                          {s.game_count} game{s.game_count === 1 ? '' : 's'} · {s.created_by_name ?? 'System'} · {whenLabel(s.created_at)}
                        </div>
                      </div>
                      {onRestore && (
                        <Button size="sm" variant="outline" onClick={() => onRestore(s)}>
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Restore
                        </Button>
                      )}
                    </li>
                  );
                }

                const b = item.batch;
                const one = b.moves.length === 1;
                const isOpen = expanded[b.batch_id] ?? false;
                const head = one
                  ? (() => {
                    const m = b.moves[0];
                    return `${gameLabel(m)} ${b.source_label} from ${spotLabel(m.from, m.from_bracket)} to ${spotLabel(m.to, m.to_bracket)}`;
                  })()
                  : `${b.source_label === 'moved on the grid' ? 'Moved' : b.source_label.replace(/^\w/, (c) => c.toUpperCase())} ${b.moves.length} games`;
                return (
                  <li key={b.batch_id} className="px-3 py-3 text-sm">
                    <div className="flex items-start gap-2">
                      {!one && (
                        <button
                          type="button"
                          className="mt-0.5 text-muted-foreground"
                          aria-label={isOpen ? 'Hide the games in this change' : 'Show the games in this change'}
                          onClick={() => setExpanded((e) => ({ ...e, [b.batch_id]: !isOpen }))}
                        >
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className={b.all_undone ? 'line-through text-muted-foreground' : ''}>{head}</div>
                        <div className="text-xs text-muted-foreground">
                          {b.actor ?? 'the system'} · {whenLabel(b.created_at)}
                          {b.all_undone && b.undone_at && (
                            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-medium text-foreground">
                              Undone {whenLabel(b.undone_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {!one && isOpen && (
                      <ul className="mt-2 space-y-1 border-l pl-5 text-xs text-muted-foreground">
                        {b.moves.map((m) => (
                          <li key={m.id} className={m.undone_at ? 'line-through' : ''}>
                            {gameLabel(m)} — {spotLabel(m.from, m.from_bracket)} → {spotLabel(m.to, m.to_bracket)}
                            {m.is_lead && b.source === 'bracket_move' ? ' (the game moved)' : ''}
                            {m.note ? ` — ${m.note}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
